import React, { useEffect } from 'react';
import { track } from '@amplitude/analytics-browser';
import { Box, Button, Card, Divider, Icon, Text } from '@nimbus-ds/components';
import { ClockIcon, MoneyIcon, PesoIcon, RepeatIcon } from '@nimbus-ds/icons';
import {
  useBankAccount,
  useCreditOffer,
  useIsMobile,
  useStoreInfo,
} from 'commons/hooks';
import { useTranslationWithPrefix } from 'commons/utils';
import { LENDING_EXTERNAL_URL } from '../consts';

function OfferBanner(): JSX.Element | null {
  const { t } = useTranslationWithPrefix('home.lendingBanner');
  const { data: creditOffer, isLoading } = useCreditOffer();
  const { data: bankAccount } = useBankAccount();
  const isMobile = useIsMobile();
  const { storeInfo } = useStoreInfo();
  const personId = storeInfo.user?.id;

  useEffect(() => {
    if (!isLoading && creditOffer?.canOffer) {
      track('pagonube_lending_awareness_payments_view', {
        person_id: personId,
      });
    }
  }, [isLoading, creditOffer?.canOffer, personId]);

  if (isLoading || !creditOffer?.canOffer) return null;

  const cuit = bankAccount?.fiscalDocument;
  const url = new URL(LENDING_EXTERNAL_URL);
  if (cuit) url.searchParams.set('document_id', cuit);
  const ctaUrl = url.toString();

  const features = [
    { icon: ClockIcon, label: t('features.approval') },
    { icon: PesoIcon, label: t('features.payment') },
    { icon: RepeatIcon, label: t('features.installments') },
  ];

  const handleCtaClick = () => {
    track('pagonube_lending_awareness_payments_click', { person_id: personId });
    window.open(ctaUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card data-testid="offer-banner">
      <Card.Body>
        <Box display="flex" flexDirection="column" gap="3">
          <Box display="flex" alignItems="flex-start" gap="3">
            <Box
              borderRadius="2"
              backgroundColor="primary-interactive"
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="36px"
              height="36px"
              minWidth="36px"
              minHeight="36px"
            >
              <Icon color="neutral-background" source={<MoneyIcon />} />
            </Box>
            <Box
              display="flex"
              flexDirection="row"
              gap={isMobile ? 'none' : '1'}
              flexWrap="wrap"
              marginTop={isMobile ? 'none' : '2'}
            >
              <Text
                fontSize={isMobile ? 'base' : 'highlight'}
                fontWeight="bold"
                color="neutral-textHigh"
              >
                {t('title')}
              </Text>
              <Text
                fontSize={isMobile ? 'caption' : 'highlight'}
                color="neutral-textHigh"
              >
                {t('subtitle')}
              </Text>
            </Box>
          </Box>
          <Divider />
          {isMobile ? (
            <Box display="flex" flexDirection="column" gap="4">
              <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gridGap="4">
                {features.map(({ icon: FeatureIcon, label }) => (
                  <Box
                    key={label}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap="2"
                  >
                    <Box
                      borderRadius="1"
                      backgroundColor="primary-surface"
                      padding="1"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Icon
                        color="primary-interactive"
                        source={<FeatureIcon />}
                      />
                    </Box>
                    <Text
                      fontSize="caption"
                      textAlign="center"
                      color="neutral-textHigh"
                    >
                      {label}
                    </Text>
                  </Box>
                ))}
              </Box>
              <Button
                appearance="primary"
                size="small"
                onClick={handleCtaClick}
                fullWidth
              >
                {t('cta')}
              </Button>
            </Box>
          ) : (
            <Box display="flex" flexDirection="row" alignItems="center" gap="4">
              <Box
                display="flex"
                flexDirection="row"
                gap={isMobile ? '4' : '12'}
                flex="1"
              >
                {features.map(({ icon: FeatureIcon, label }) => (
                  <Box key={label} display="flex" alignItems="center" gap="2">
                    <Box
                      borderRadius="1"
                      backgroundColor="primary-surface"
                      padding="1"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Icon
                        color="primary-interactive"
                        source={<FeatureIcon />}
                      />
                    </Box>
                    <Text fontSize="base" color="neutral-textHigh">
                      {label}
                    </Text>
                  </Box>
                ))}
              </Box>
              <Box minWidth="280px">
                <Button
                  appearance="primary"
                  size="small"
                  onClick={handleCtaClick}
                  fullWidth
                >
                  {t('cta')}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Card.Body>
    </Card>
  );
}

export default OfferBanner;
